/**
 * Ana sayfanın ek bölümleri.
 *
 * İlk denemede sayfa "eski sayfanın koyu hâli" gibi kalmıştı: aynı içerik, aynı
 * kart ızgarası, resim yok. Bu dosya sayfaya farklı DÜZENLER getiriyor —
 * numaralı süreç, karşılaştırma tablosu, açılır SSS — ki her bölüm göz için
 * ayrı bir ritim olsun.
 */
import React, { useState } from 'react';
import { Check, X, Plus, Minus, PackageCheck, Search, MessageCircle, HandCoins } from 'lucide-react';
import { cn } from '../../lib/utils';

/* ═══════════════════════ SÜREÇ ═══════════════════════ */

const ADIMLAR = [
  {
    ikon: PackageCheck,
    baslik: 'Cihazı kaydet',
    metin: 'Müşteri ve cihaz bilgisi tek ekranda. QR etiket bas, cihaz raftayken bile kimin olduğu belli olsun.',
  },
  {
    ikon: Search,
    baslik: 'Teşhis ve onay',
    metin: 'Arıza tespitini yaz, parça ekle, tutarı çıkar. Müşteriye onay bağlantısı gitsin — telefonda uğraşma.',
  },
  {
    ikon: MessageCircle,
    baslik: 'Onarım ve bildirim',
    metin: 'İş bitince müşteriye otomatik SMS/WhatsApp. "Cihazım hazır mı?" telefonları kesilir.',
  },
  {
    ikon: HandCoins,
    baslik: 'Teslim ve tahsilat',
    metin: 'Teslimde tahsilatı işle; cari, kasa ve stok aynı anda güncellensin. Ay sonu mutabakat derdi bitsin.',
  },
];

export const SurecBolumu: React.FC = () => (
  <section className="border-t border-slate-200 py-12 md:py-16">
    <div className="mx-auto max-w-6xl px-5">
      <div className="max-w-2xl">
        <span className="text-xs font-semibold uppercase tracking-[0.14em] text-vurgu-600">Nasıl çalışır</span>
        <h2 className="mt-3 text-balance text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
          Cihaz kabulünden tahsilata, dört adım
        </h2>
        <p className="mt-4 text-[16px] leading-relaxed text-slate-600">
          Her adımda girdiğiniz bilgi bir sonrakine taşınır. Aynı veriyi ikinci kez yazmazsınız.
        </p>
      </div>

      <ol className="mt-9 grid gap-px overflow-hidden rounded-2xl border border-zemin-600 bg-zemin-600 p-0 md:grid-cols-2 lg:grid-cols-4">
        {ADIMLAR.map(({ ikon: I, baslik, metin }, i) => (
          <li key={baslik} className="relative bg-zemin-800 p-5">
            <span className="font-mono text-[11px] font-bold text-vurgu-600">
              {String(i + 1).padStart(2, '0')}
            </span>
            <span className="mt-4 grid h-11 w-11 place-items-center rounded-xl bg-vurgu-500/10 ring-1 ring-vurgu-500/20">
              <I className="h-[22px] w-[22px] text-vurgu-400" />
            </span>
            <h3 className="mt-4 text-[17px] font-semibold tracking-tight text-slate-900">{baslik}</h3>
            <p className="mt-2 text-[14px] leading-relaxed text-slate-600">{metin}</p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

/* ═══════════════════════ KARŞILAŞTIRMA ═══════════════════════ */

const SATIRLAR: { konu: string; once: string; sonra: string }[] = [
  { konu: 'Cihaz nerede?',        once: 'Deftere bak, rafta ara',            sonra: 'Servis no ile anında bul' },
  { konu: 'Müşteri arıyor',       once: '"Bakıp döneyim"',                   sonra: 'Durum ekranda, anında cevap' },
  { konu: 'Cari bakiye',          once: 'Excel dosyası, elle toplama',        sonra: 'Dekontlardan otomatik' },
  { konu: 'Stok düşümü',          once: 'Unutulur, sayımda fark çıkar',       sonra: 'Satışta kendiliğinden düşer' },
  { konu: 'Tahsilat takibi',      once: 'Kim ne kadar borçlu belirsiz',       sonra: 'Vadesi geçenler listede' },
  { konu: 'Ay sonu raporu',       once: 'Bir gün masa başı',                  sonra: 'Tek tıkla hazır' },
];

export const KarsilastirmaBolumu: React.FC = () => (
  <section className="border-t border-slate-200 bg-slate-50/70 py-12 md:py-16">
    <div className="mx-auto max-w-5xl px-5">
      <div className="mx-auto max-w-2xl text-center">
        <span className="text-xs font-semibold uppercase tracking-[0.14em] text-vurgu-600">Fark</span>
        <h2 className="mt-3 text-balance text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
          Defter ve Excel'le takip etmek yerine
        </h2>
      </div>

      <div className="mt-9 overflow-hidden rounded-2xl border border-zemin-600">
        <div className="grid grid-cols-[1fr_1fr] border-b border-zemin-600 bg-slate-100 sm:grid-cols-[1.1fr_1fr_1fr]">
          <div className="hidden px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500 sm:block" />
          <div className="flex items-center gap-2 px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <X className="h-3.5 w-3.5 text-red-400/70" /> Elle takip
          </div>
          <div className="flex items-center gap-2 px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-vurgu-600">
            <Check className="h-3.5 w-3.5" /> tservis ile
          </div>
        </div>

        {SATIRLAR.map((s, i) => (
          <div
            key={s.konu}
            className={cn(
              'grid grid-cols-[1fr_1fr] border-b border-slate-200 last:border-0 sm:grid-cols-[1.1fr_1fr_1fr]',
              i % 2 ? 'bg-slate-50' : 'bg-zemin-800',
            )}
          >
            <div className="col-span-2 px-5 pb-1 pt-3 text-[13px] font-semibold text-slate-900 sm:col-span-1 sm:py-4">
              {s.konu}
            </div>
            <div className="px-5 pb-3 pt-1 text-[13px] text-slate-500 sm:py-4">{s.once}</div>
            <div className="px-5 pb-3 pt-1 text-[13px] text-slate-700 sm:py-4">{s.sonra}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ═══════════════════════ SSS ═══════════════════════ */

const SORULAR = [
  {
    s: 'Kurulum gerekiyor mu?',
    c: 'Hayır. tservis tarayıcıdan çalışır — bilgisayar, tablet ve telefondan girebilirsiniz. Sunucu, yedekleme ve güncelleme bizim işimiz.',
  },
  {
    s: 'Verilerim güvende mi, yedek alınıyor mu?',
    c: 'Veritabanı her gece otomatik yedeklenir ve yedekler saklanır. Bağlantı SSL ile şifrelenir; her işletmenin verisi kendi hesabında ayrı tutulur.',
  },
  {
    s: 'Kaç kullanıcı ekleyebilirim?',
    c: 'Sınırsız. Personel sayısına göre ek ücret yok. Her personele hangi ekranları görebileceğini ayrı ayrı belirleyebilirsiniz.',
  },
  {
    s: 'Mevcut müşteri listemi aktarabilir miyim?',
    c: 'Evet. Excel listenizi gönderin, ilk kurulumda biz aktaralım. Cihaz geçmişi ve açık bakiyeler de taşınabilir.',
  },
  {
    s: 'Deneme süresi sonunda ne oluyor?',
    c: '14 gün boyunca tüm özellikler açık, kredi kartı istemiyoruz. Süre bitince devam etmezseniz hesap dondurulur; verileriniz silinmez.',
  },
  {
    s: 'İptal etmek istersem?',
    c: 'İstediğiniz zaman iptal edersiniz, taahhüt yok. İptalde verilerinizi Excel olarak dışa aktarabilirsiniz.',
  },
];

export const SssBolumu: React.FC = () => {
  const [acik, setAcik] = useState<number | null>(0);

  return (
    <section className="border-t border-slate-200 py-12 md:py-16">
      <div className="mx-auto max-w-3xl px-5">
        <div className="text-center">
          <span className="text-xs font-semibold uppercase tracking-[0.14em] text-vurgu-600">Sık sorulanlar</span>
          <h2 className="mt-3 text-balance text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
            Merak edilenler
          </h2>
        </div>

        <div className="mt-8 divide-y divide-slate-200 overflow-hidden rounded-2xl border border-zemin-600 bg-zemin-800">
          {SORULAR.map((q, i) => {
            const acikMi = acik === i;
            return (
              <div key={q.s}>
                <button
                  onClick={() => setAcik(acikMi ? null : i)}
                  aria-expanded={acikMi}
                  className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-slate-50"
                >
                  <span className="flex-1 text-[15px] font-medium text-slate-900">{q.s}</span>
                  {acikMi
                    ? <Minus className="h-4 w-4 shrink-0 text-vurgu-400" />
                    : <Plus className="h-4 w-4 shrink-0 text-slate-500" />}
                </button>
                {acikMi && (
                  <p className="px-5 pb-5 text-[14px] leading-relaxed text-slate-600">{q.c}</p>
                )}
              </div>
            );
          })}
        </div>

        <p className="mt-6 text-center text-sm text-slate-500">
          Başka sorunuz mu var?{' '}
          <a href="mailto:info@tservis.tr" className="font-medium text-vurgu-600 no-underline hover:underline">
            info@tservis.tr
          </a>
        </p>
      </div>
    </section>
  );
};
